import { useEffect, useState } from "react";
import { Users, Clock, CheckCircle2, XCircle, IndianRupee, Wallet, Settings, CreditCard, Landmark, Globe, TrendingUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { StatCard, PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const TABS = [
  { k: "revenue", l: "CRM Revenue", icon: TrendingUp },
  { k: "payments", l: "CRM Payments", icon: CreditCard },
  { k: "subscribers", l: "Subscribers", icon: Users },
  { k: "settings", l: "CRM Pricing & Settings", icon: Settings },
];

const PAY_FILTERS = ["", "pending", "needs_info", "approved", "rejected"];

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div data-testid={testid} className="bg-card border border-border rounded-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export default function RootCrm() {
  const [tab, setTab] = useState("revenue");
  const [rev, setRev] = useState(null);
  const [webRev, setWebRev] = useState(null);
  const [payments, setPayments] = useState(null);
  const [payFilter, setPayFilter] = useState("pending");
  const [kind, setKind] = useState("");
  const [month, setMonth] = useState("");
  const [subs, setSubs] = useState(null);
  const [settings, setSettings] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [modal, setModal] = useState(null);
  const [note, setNote] = useState("");
  const [payForm, setPayForm] = useState({ amount: "", method: "", reference: "", notes: "" });
  const [stateForm, setStateForm] = useState({ status: "active", days: 30 });
  const [busy, setBusy] = useState(false);

  const loadRevenue = () => {
    api.get("/root/crm/revenue").then((r) => setRev(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/root/self-service/overview").then((r) => setWebRev(r.data)).catch(() => {});
    api.get("/root/crm/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  };
  const loadPayments = () => {
    const qs = [payFilter && `status=${payFilter}`, kind && `kind=${kind}`, month && `month=${month}`].filter(Boolean).join("&");
    api.get(`/root/crm/payments${qs ? `?${qs}` : ""}`).then((r) => setPayments(r.data.payments)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { loadRevenue(); }, []);
  useEffect(() => {
    if (tab === "payments") { setPayments(null); loadPayments(); }
    if (tab === "subscribers" && !subs) api.get("/root/crm/subscribers").then((r) => setSubs(r.data.subscribers)).catch((e) => toast.error(errMsg(e)));
    if (tab === "settings" && !settings) api.get("/root/crm/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tab, payFilter, kind, month]);

  const approve = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/root/crm/payments/${p.id}/approve`);
      toast.success(`Approved — commission ${fmtINR(data.commission)} recorded`);
      loadPayments(); loadRevenue(); setSubs(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitNote = async () => {
    setBusy(true);
    try {
      await api.post(`/root/crm/payments/${modal.payment.id}/${modal.mode}`, { note });
      toast.success(modal.mode === "reject" ? "Payment rejected" : "Customer asked for more info");
      setModal(null); setNote("");
      loadPayments(); loadRevenue();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/root/crm/settings", settings);
      setSettings(data);
      toast.success("CRM settings saved");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const recordPayout = async () => {
    setBusy(true);
    try {
      await api.post("/root/partner-payouts", { partner_id: modal.partner.partner_id, ...payForm, amount: Number(payForm.amount), source: "crm" });
      toast.success("CRM payout recorded — partner notified");
      setModal(null); setPayForm({ amount: "", method: "", reference: "", notes: "" });
      loadRevenue();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setState = async () => {
    setBusy(true);
    try {
      await api.post("/root/crm/set-state", { wid: modal.sub.wid, ...stateForm, days: Number(stateForm.days) });
      toast.success(`Test state set: ${stateForm.status}`);
      setModal(null); setSubs(null);
      api.get("/root/crm/subscribers").then((r) => setSubs(r.data.subscribers));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="root-crm-page">
      <PageHeader title="CRM Revenue & Subscriptions" subtitle="Separate CRM module — pricing, manual payment verification, renewals, partner commissions." testid="root-crm-title" />

      <div className="flex gap-2 flex-wrap mb-8">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`crm-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${tab === t.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "revenue" && (
        !rev ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={4} /></div> : (
          <div className="space-y-10">
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Platform Revenue Streams</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <StatCard icon={Globe} label="Website Revenue (Self-Service)" value={fmtINR(webRev?.gross || 0)} sub={`Commission ${fmtINR(webRev?.commission || 0)}`} testid="crm-rev-website" />
                <StatCard icon={IndianRupee} label="CRM Revenue" value={fmtINR(rev.total_revenue)} sub={`This month ${fmtINR(rev.monthly_revenue)}`} testid="crm-rev-crm" delay={0.05} />
                <StatCard icon={TrendingUp} label="Total Platform Revenue" value={fmtINR((webRev?.gross || 0) + rev.total_revenue)} sub={`Company share ${fmtINR((webRev?.company_revenue || 0) + rev.company_revenue)}`} testid="crm-rev-total" delay={0.1} tint="bg-emerald-500/10 text-emerald-600" />
              </div>
            </div>

            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-4">CRM Breakdown</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <StatCard icon={Users} label="Active Subscribers" value={rev.active} sub={`${rev.subscribers} total`} testid="crm-stat-active" />
                <StatCard icon={Clock} label="Pending Payments" value={rev.pending_payments} testid="crm-stat-pending" delay={0.05} />
                <StatCard icon={XCircle} label="Expired" value={rev.expired} testid="crm-stat-expired" delay={0.1} />
                <StatCard icon={RefreshCw} label="Renewals" value={rev.renewals} sub={`${rev.new_subscriptions} new`} testid="crm-stat-renewals" delay={0.15} />
                <StatCard icon={IndianRupee} label="Monthly CRM Revenue" value={fmtINR(rev.monthly_revenue)} testid="crm-stat-monthly" delay={0.2} />
                <StatCard icon={Wallet} label="Partner Commission" value={fmtINR(rev.commission_total)} sub={`Pending ${fmtINR(rev.commission_pending)}`} testid="crm-stat-commission" delay={0.25} />
                <StatCard icon={CheckCircle2} label="Commission Paid" value={fmtINR(rev.commission_paid)} testid="crm-stat-comm-paid" delay={0.3} />
                <StatCard icon={Landmark} label="Company CRM Revenue" value={fmtINR(rev.company_revenue)} testid="crm-stat-company" delay={0.35} />
              </div>
            </div>

            {payouts && (
              <div>
                <h2 className="font-display font-bold text-2xl tracking-tight mb-4">CRM Partner Payouts</h2>
                {payouts.partners.length === 0 ? (
                  <p className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-6">No CRM commissions yet — they appear after the first approved CRM payment.</p>
                ) : (
                  <TableShell head={["Partner", "Bank / UPI", "CRM Earned", "Paid", "Pending", "Action"]} testid="crm-payouts-table">
                    {payouts.partners.map((p) => (
                      <TR key={p.partner_id} testid={`crm-payout-${p.partner_id}`}>
                        <TD><p className="font-semibold">{p.partner_name}</p><p className="text-xs text-muted-foreground">{p.partner_email}</p></TD>
                        <TD className="text-xs text-muted-foreground">
                          {p.payout_details?.upi_id ? <p>UPI: {p.payout_details.upi_id}</p> : null}
                          {p.payout_details?.account_number ? <p>A/C {p.payout_details.account_number} · {p.payout_details.ifsc}</p> : null}
                          {!p.payout_details?.upi_id && !p.payout_details?.account_number && "—"}
                        </TD>
                        <TD className="font-semibold">{fmtINR(p.earned)}</TD>
                        <TD>{fmtINR(p.paid)}</TD>
                        <TD className="font-bold text-amber-600 dark:text-amber-400">{fmtINR(p.pending)}</TD>
                        <TD>
                          <button data-testid={`crm-record-payout-${p.partner_id}`} disabled={p.pending <= 0}
                            onClick={() => { setModal({ mode: "payout", partner: p }); setPayForm({ amount: p.pending, method: "", reference: "", notes: "" }); }}
                            className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed">
                            Record Payout
                          </button>
                        </TD>
                      </TR>
                    ))}
                  </TableShell>
                )}
                {payouts.payouts.length > 0 && (
                  <div className="mt-6">
                    <p className="overline-label mb-3">CRM Payout History</p>
                    <TableShell head={["Amount", "Method", "Reference", "Notes", "Date"]} testid="crm-payout-history">
                      {payouts.payouts.map((p) => (
                        <TR key={p.id} testid={`crm-payout-row-${p.id}`}>
                          <TD className="font-bold">{fmtINR(p.amount)}</TD>
                          <TD>{p.method || "—"}</TD>
                          <TD className="font-mono text-xs">{p.reference || "—"}</TD>
                          <TD className="text-sm text-muted-foreground">{p.notes || "—"}</TD>
                          <TD className="text-sm text-muted-foreground">{fmtDateTime(p.created_at)}</TD>
                        </TR>
                      ))}
                    </TableShell>
                  </div>
                )}
              </div>
            )}

            {rev.price_history?.length > 0 && (
              <div>
                <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Price Change History</h2>
                <TableShell head={["Old Price", "New Price", "Changed By", "Date"]} testid="crm-price-history">
                  {rev.price_history.map((h) => (
                    <TR key={h.id} testid={`price-history-${h.id}`}>
                      <TD>{fmtINR(h.old)}</TD>
                      <TD className="font-bold">{fmtINR(h.new)}</TD>
                      <TD className="text-sm text-muted-foreground">{h.changed_by_email}</TD>
                      <TD className="text-sm text-muted-foreground">{fmtDateTime(h.created_at)}</TD>
                    </TR>
                  ))}
                </TableShell>
              </div>
            )}
          </div>
        )
      )}

      {tab === "payments" && (
        <div>
          <div className="flex gap-2 flex-wrap mb-4 items-center">
            {PAY_FILTERS.map((f) => (
              <button key={f || "all"} data-testid={`crm-pay-filter-${f || "all"}`} onClick={() => setPayFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors ${payFilter === f ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {f ? f.replace(/_/g, " ") : "all"}
              </button>
            ))}
            <select data-testid="crm-kind-filter" value={kind} onChange={(e) => setKind(e.target.value)}
              className="h-8 rounded-full border border-border bg-background px-3 text-xs font-bold">
              <option value="">New + Renewal</option>
              <option value="new">New Subscription</option>
              <option value="renewal">Renewal</option>
            </select>
            <Input data-testid="crm-month-filter" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-40 rounded-full text-xs" />
          </div>
          {payments === null ? (
            <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div>
          ) : payments.length === 0 ? (
            <EmptyState icon={CreditCard} title="No CRM payments here" body="CRM payment submissions matching this filter will appear here." />
          ) : (
            <TableShell head={["Customer", "Business", "Partner", "Amount", "Reference", "Month", "Type", "Status", "Submitted", "Actions"]} testid="crm-payments-table">
              {payments.map((p) => (
                <TR key={p.id} testid={`crm-payment-${p.id}`}>
                  <TD><p className="font-semibold">{p.customer_name}</p><p className="text-xs text-muted-foreground">{p.customer_email}</p></TD>
                  <TD>{p.website_name || "—"}</TD>
                  <TD>{p.partner_name || "—"}</TD>
                  <TD className="font-bold">{fmtINR(p.amount)}</TD>
                  <TD className="font-mono text-xs">{p.reference || "—"}</TD>
                  <TD className="text-xs">{p.renewal_month || "—"}</TD>
                  <TD><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${p.kind === "renewal" ? "bg-blue-500/10 text-blue-600 dark:text-blue-400" : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"}`}>{p.kind}</span></TD>
                  <TD><StatusBadge status={p.status} /></TD>
                  <TD className="text-sm text-muted-foreground">{fmtDateTime(p.created_at)}</TD>
                  <TD>
                    {["pending", "needs_info"].includes(p.status) ? (
                      <div className="flex gap-2">
                        <button data-testid={`crm-approve-${p.id}`} disabled={busy} onClick={() => approve(p)}
                          className="h-8 px-3 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-bold hover:bg-emerald-500/25 transition-colors">Approve</button>
                        <button data-testid={`crm-needsinfo-${p.id}`} disabled={busy} onClick={() => { setModal({ mode: "needs-info", payment: p }); setNote(""); }}
                          className="h-8 px-3 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400 text-xs font-bold hover:bg-amber-500/25 transition-colors">Needs Info</button>
                        <button data-testid={`crm-reject-${p.id}`} disabled={busy} onClick={() => { setModal({ mode: "reject", payment: p }); setNote(""); }}
                          className="h-8 px-3 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 text-xs font-bold hover:bg-red-500/25 transition-colors">Reject</button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">{p.commission != null ? `Commission ${fmtINR(p.commission)}` : p.admin_note || "—"}</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      {tab === "subscribers" && (
        !subs ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> :
        subs.length === 0 ? (
          <EmptyState icon={Users} title="No CRM subscribers yet" body="When business owners subscribe to CRM, each subscription appears here." />
        ) : (
          <TableShell head={["Business", "Owner", "Partner", "Status", "Price", "Last Payment", "Next Renewal", "Total Paid", "Renewals", "Commission", "Company", "Test"]} testid="crm-subscribers-table">
            {subs.map((s) => (
              <TR key={s.wid} testid={`crm-sub-${s.wid}`}>
                <TD className="font-semibold">{s.business}</TD>
                <TD><p>{s.owner}</p><p className="text-xs text-muted-foreground">{s.owner_email}</p></TD>
                <TD>{s.partner}</TD>
                <TD><StatusBadge status={s.status} /></TD>
                <TD>{fmtINR(s.price_monthly)}/mo</TD>
                <TD className="text-sm text-muted-foreground">{s.last_payment ? fmtDateTime(s.last_payment) : "—"}</TD>
                <TD className="text-sm">{s.expires_at ? fmtDateTime(s.expires_at).split(",")[0] : "—"}</TD>
                <TD className="font-semibold">{fmtINR(s.total_paid)}</TD>
                <TD>{s.renewals}</TD>
                <TD>{fmtINR(s.commission)}</TD>
                <TD>{fmtINR(s.company_revenue)}</TD>
                <TD>
                  <button data-testid={`crm-setstate-${s.wid}`} onClick={() => { setModal({ mode: "setstate", sub: s }); setStateForm({ status: "active", days: 30 }); }}
                    className="h-8 px-3 rounded-lg border border-border text-xs font-bold hover:bg-muted transition-colors">Set State</button>
                </TD>
              </TR>
            ))}
          </TableShell>
        )
      )}

      {tab === "settings" && (
        !settings ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> : (
          <form data-testid="crm-settings-form" onSubmit={saveSettings} className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-2 gap-5 max-w-4xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="crm-set-enabled" type="checkbox" checked={Boolean(settings.enabled)} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} className="h-5 w-5 accent-primary" />
              <span className="text-sm font-bold">CRM subscriptions enabled</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="crm-set-testmode" type="checkbox" checked={Boolean(settings.test_mode)} onChange={(e) => setSettings({ ...settings, test_mode: e.target.checked })} className="h-5 w-5 accent-primary" />
              <span className="text-sm font-bold">Test mode <span className="text-xs font-normal text-muted-foreground">(allow test activations without real payment)</span></span>
            </label>
            <div className="space-y-1.5">
              <Label className="overline-label">CRM Monthly Price (₹)</Label>
              <Input data-testid="crm-set-price" type="number" min="0" value={settings.price} onChange={(e) => setSettings({ ...settings, price: e.target.value })} className="h-12 rounded-md bg-background" />
              <p className="text-[11px] text-muted-foreground">Changing price affects only new payments — old records stay unchanged.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">SuperProfile Payment Link</Label>
              <Input data-testid="crm-set-payment-link" value={settings.payment_link} onChange={(e) => setSettings({ ...settings, payment_link: e.target.value })} className="h-12 rounded-md bg-background" placeholder="https://superprofile.bio/..." />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Partner Commission Type</Label>
              <select data-testid="crm-set-commission-type" value={settings.commission_type} onChange={(e) => setSettings({ ...settings, commission_type: e.target.value })} className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="fixed">Fixed amount (₹)</option>
                <option value="percent">Percentage (%)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Partner Commission {settings.commission_type === "percent" ? "(%)" : "(₹)"}</Label>
              <Input data-testid="crm-set-commission-value" type="number" min="0" value={settings.commission_value} onChange={(e) => setSettings({ ...settings, commission_value: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="overline-label">Verification Message (shown to customer)</Label>
              <textarea data-testid="crm-set-verification-message" value={settings.verification_message} onChange={(e) => setSettings({ ...settings, verification_message: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-20" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="overline-label">Subscription Description</Label>
              <textarea data-testid="crm-set-description" value={settings.description || ""} onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-20" />
            </div>
            <div className="sm:col-span-2">
              <Button data-testid="crm-settings-save" type="submit" disabled={busy} className="rounded-full font-bold">{busy ? "Saving..." : "Save Changes"}</Button>
            </div>
          </form>
        )
      )}

      {modal && ["reject", "needs-info"].includes(modal.mode) && (
        <Modal testid="crm-note-modal" title={modal.mode === "reject" ? `Reject CRM payment · ${fmtINR(modal.payment.amount)}` : "Request more information"} onClose={() => setModal(null)}>
          <p className="text-sm text-muted-foreground">
            {modal.mode === "reject"
              ? `Reject ${modal.payment.customer_name}'s CRM payment? The customer will be notified and can resubmit.`
              : `Ask ${modal.payment.customer_name} for additional payment details.`}
          </p>
          <textarea data-testid="crm-note-input" value={note} onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-24" placeholder="Note for the customer (optional but recommended)" />
          <div className="flex gap-3 justify-end">
            <Button variant="outline" data-testid="crm-note-cancel" onClick={() => setModal(null)} className="rounded-full font-bold">Cancel</Button>
            <Button data-testid="crm-note-confirm" disabled={busy} onClick={submitNote}
              className={`rounded-full font-bold ${modal.mode === "reject" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}>
              {busy ? "Working..." : "Confirm"}
            </Button>
          </div>
        </Modal>
      )}

      {modal?.mode === "payout" && (
        <Modal testid="crm-payout-modal" title={`Record CRM payout · ${modal.partner.partner_name}`} onClose={() => setModal(null)}>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="overline-label">Amount (₹) — pending {fmtINR(modal.partner.pending)}</Label>
              <Input data-testid="crm-payout-amount" type="number" min="1" max={modal.partner.pending} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Method</Label>
              <Input data-testid="crm-payout-method" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="h-12 rounded-md bg-background" placeholder="UPI / Bank Transfer" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Reference / UTR</Label>
              <Input data-testid="crm-payout-reference" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Notes</Label>
              <Input data-testid="crm-payout-notes" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" data-testid="crm-payout-cancel" onClick={() => setModal(null)} className="rounded-full font-bold">Cancel</Button>
            <Button data-testid="crm-payout-confirm" disabled={busy || !payForm.amount} onClick={recordPayout} className="rounded-full font-bold">{busy ? "Saving..." : "Record Payout"}</Button>
          </div>
        </Modal>
      )}

      {modal?.mode === "setstate" && (
        <Modal testid="crm-setstate-modal" title={`Test state · ${modal.sub.business}`} onClose={() => setModal(null)}>
          <p className="text-sm text-muted-foreground">Testing only — instantly switch this subscription state without payment. CRM data is never deleted.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="overline-label">Status</Label>
              <select data-testid="crm-setstate-status" value={stateForm.status} onChange={(e) => setStateForm({ ...stateForm, status: e.target.value })} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="locked">Locked</option>
                <option value="cancelled">Cancelled</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Days (active)</Label>
              <Input data-testid="crm-setstate-days" type="number" min="1" value={stateForm.days} onChange={(e) => setStateForm({ ...stateForm, days: e.target.value })} className="h-11 rounded-md bg-background" />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" data-testid="crm-setstate-cancel" onClick={() => setModal(null)} className="rounded-full font-bold">Cancel</Button>
            <Button data-testid="crm-setstate-confirm" disabled={busy} onClick={setState} className="rounded-full font-bold">{busy ? "Working..." : "Apply"}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
